import { io, Socket } from 'socket.io-client';

/** Invoked when the server acknowledges this emit (Socket.IO callback / ack). */
export type RealTimeSendAck = (...responseArgs: unknown[]) => void;

/** A "watch ..." request that has to be re-sent on every connection. */
type Subscription = { eventName: string; payload: unknown; callers: Map<symbol, RealTimeSendAck | undefined> };

// This is not used anywhere
export enum RealTimeEvents {
	ACTIVITY_CHANGE = 'activity change',
	WAY_POINT_ARRIVED = 'way point arrived',
	WAY_POINT_DONE = 'way point done',
	WAY_POINT_ETA_UPDATE = 'way point eta updated',
	WAY_POINT_LOCATION_UPDATE = 'way point location updated',
	ORDER_DONE = 'order done',
	ORDER_CANCELED = 'order cancelled',
	LOCATION_UPDATE = 'location update',
	WATCH_ORDER = 'watch order',
	WATCH_DRIVER = 'watch driver',
	WATCH_WAY_POINT = 'watch way point',
	CONNECT = 'connect'
}

export default class RealTimeService {
	_socket: Socket;
	realTimeUrl: string;
	socketWebsocketPort: number;
	securedSockets: boolean;
	private _onVisibilityChange: () => void;
	private _subscriptions = new Map<string, Subscription>();
	private _generation = 0;

	constructor(realTimeUrl: string, socketWebsocketPort?: number, securedSockets?: boolean) {
		this.realTimeUrl = realTimeUrl;
		this.socketWebsocketPort = socketWebsocketPort ? socketWebsocketPort : 443;
		this.securedSockets = securedSockets ? securedSockets : true;
		this._onVisibilityChange = () => this.reconnectIfNeeded();
	}

	/** Bumped on every {@link init}, so callers that cache their own subscription state (e.g. {@link RealtimeWatchTracker}) can tell a stale entry from a live one instead of trusting it blindly. */
	get generation(): number {
		return this._generation;
	}

	init() {
		// Subscriptions belong to the socket they were registered for: every watch in the flow is
		// registered after init, so anything still here is from the previous connection/order.
		this._generation++;
		this._subscriptions.clear();

		if (this._socket) {
			this._socket.disconnect();
		}

		this._socket = io(this.realTimeUrl, {
			transports: ['websocket'],
			transportOptions: {
				websocket: {
					port: this.socketWebsocketPort,
					secure: this.securedSockets
				}
			}
		});

		this.registerConnectHandler();

		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', this._onVisibilityChange);
		}
	}

	/** Resends active subscriptions on every connection. */
	private registerConnectHandler() {
		this._socket.on('connect', () => this.sendSubscriptions());
	}

	private reconnectIfNeeded() {
		if (typeof document === 'undefined' || document.visibilityState !== 'visible' || !this._socket) {
			return;
		}
		if (!this._socket.connected) {
			this._socket.connect();
		}
	}

	/**
	 * Emit a realtime event (same semantics as {@link Socket.emit}).
	 * When provided, `ack` is invoked after the server runs the acknowledgement for this emit.
	 * @see https://socket.io/docs/v4/client-api/#socketemiteventname-args
	 */
	send(eventName: string): void;
	send(eventName: string, payload: unknown, ack?: RealTimeSendAck): void;
	send(eventName: string, ...args: any[]): void {
		this._socket.emit(eventName, ...args);
	}

	/**
	 * Register a "watch ..." request. Unlike {@link send}, the emit is repeated on every
	 * connection: the server keeps watchers per connection, so after a reconnect (mobile Safari
	 * suspending the websocket while backgrounded, flaky network, ...) a connection that was never
	 * told what to watch stays silent and tracking is stuck until the user reloads.
	 *
	 * The request is emitted immediately when already connected, and otherwise on the next
	 * 'connect' - never both, so a watch is sent exactly once per connection.
	 *
	 * `caller` identifies the owner of this watch (pass a stable per-instance symbol) so
	 * {@link unwatch} can drop just that owner's claim without affecting other owners that
	 * happen to watch the same event+payload - see the dedup note below.
	 */
	watch(eventName: string, payload: unknown, caller: symbol, ack?: RealTimeSendAck): void {
		const key = this.subscriptionKey(eventName, payload);
		let subscription = this._subscriptions.get(key);
		const isNewSubscription = !subscription;

		if (!subscription) {
			subscription = { eventName, payload, callers: new Map() };
			this._subscriptions.set(key, subscription);
		}

		subscription.callers.set(caller, ack);

		if (isNewSubscription && this._socket?.connected) {
			this.sendSubscription(subscription);
		}
	}

	/** Drop `caller`'s claim on a watch registered via {@link watch}; the subscription is only removed once every caller has unwatched it. */
	unwatch(eventName: string, payload: unknown, caller: symbol): void {
		const key = this.subscriptionKey(eventName, payload);
		const subscription = this._subscriptions.get(key);

		if (!subscription) {
			return;
		}

		subscription.callers.delete(caller);

		if (subscription.callers.size === 0) {
			this._subscriptions.delete(key);
		}
	}

	/** Builds a dedup key stable regardless of the payload's property insertion order. */
	private subscriptionKey(eventName: string, payload: unknown): string {
		const normalizedPayload =
			payload && typeof payload === 'object' && !Array.isArray(payload)
				? Object.keys(payload as object)
						.sort()
						.reduce(
							(acc, key) => {
								acc[key] = (payload as Record<string, unknown>)[key];
								return acc;
							},
							{} as Record<string, unknown>
						)
				: payload;

		return `${eventName}:${JSON.stringify(normalizedPayload)}`;
	}

	private sendSubscriptions() {
		this._subscriptions.forEach(subscription => this.sendSubscription(subscription));
	}

	private sendSubscription({ eventName, payload, callers }: Subscription) {
		const hasAck = [...callers.values()].some(ack => !!ack);

		if (hasAck) {
			const key = this.subscriptionKey(eventName, payload);

			this.send(eventName, payload, (...args) => {
				// Look up the subscription again instead of using the `callers` snapshot: by the time the
				// server ack arrives, `unwatch`/`disposeAll` may have dropped callers (or the whole
				// subscription), and new callers may have joined - only still-registered acks should fire.
				const currentSubscription = this._subscriptions.get(key);

				currentSubscription?.callers.forEach(ack => {
					try {
						ack?.(...args);
					} catch (error) {
						console.error('Realtime subscription ack callback threw', error);
					}
				});
			});
		} else {
			this.send(eventName, payload);
		}
	}

	listen(eventName: string, fn: Function) {
		//@ts-ignore Type of evnet listener is more complex in socket, and it's complex to-reuse this type here
		this._socket.on(eventName, fn);
	}

	removeEvent(eventName: string, eventFunction?: (...args: any[]) => void) {
		this._socket.off(eventName, eventFunction);
	}
}
